import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createLevel as createLevelService,
  updateLevel as updateLevelService,
  deleteLevel as deleteLevelService, 
  getLevels
} from "../../services/curriculum/level.service";
import { validate } from "../../utils";
import { handlerErrorV2 } from "../../utils/errors";
import { changedCurriculum, updateLevelsList, updateLevelsListMethod } from "../../store/actions/curriculum.actions";
import isEmpty from 'lodash/isEmpty';
import { useParams } from "react-router-dom";

const useLevels = () => {
  const { curriculumId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchLevels = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const response = await getLevels(curriculumId);
      dispatch(updateLevelsList(response?.data || []));
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred'});
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchLevels();
  }, []);

  const validateForm = (form) => {
    const fieldRules = {
      code: 'required',
      name: 'required',
      shortName: 'required',
      position: 'required|numeric'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const createLevel = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await createLevelService(curriculumId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateLevelsListMethod('POST', response.data));
        enqueueSnackbar('Level created successfully', { variant: 'success' })
        dispatch(changedCurriculum());
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateLevel = async (levelId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await updateLevelService(curriculumId, levelId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateLevelsListMethod('PUT', response.data));
        dispatch(changedCurriculum());
        enqueueSnackbar('Level updated successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteLevel = async (levelId) => {
    try {
      const response = await deleteLevelService(curriculumId, levelId);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateLevelsListMethod('DELETE', levelId));
        dispatch(changedCurriculum());
        enqueueSnackbar('Level deleted successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  return {
    errors,
    isLoadingList,
    createLevel,
    updateLevel,
    deleteLevel
  }
};

export default useLevels;